import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Search, 
  Menu, 
  X, 
  User, 
  Heart, 
  Clock, 
  BookOpen, 
  Crown, 
  LogOut,
  PenLine,
  Settings,
  Radio,
  Rss,
  Bell,
  Mail,
  MessageSquare,
  Award,
  FileText,
  Sun,
  Moon,
  ShoppingBag,
} from 'lucide-react';

export default function Navbar() {
  const { user, logout, unreadCount, im } = useAuth();
  const showEnvelope = user && (im.count > 0 || (im.eligible && (!im.decided || im.promo_enabled)));
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [livrosPublished, setLivrosPublished] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/livros/section-status').then(({ data }) => {
      if (!cancelled) setLivrosPublished(data?.status === 'published');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { href: '/home', label: 'Início' },
    { href: '/browse', label: 'Contos' },
    { href: '/confessions', label: 'Confissões' },
    { href: '/series', label: 'Séries' },
    { href: '/prazer', label: '+ Prazer' },
    { href: '/conhece-te', label: 'Conhece-te' },
    ...(livrosPublished ? [{ href: '/livros', label: 'Livros' }] : []),
    { href: '/loja', label: 'LOJA' },
  ];

  return (
    <nav className={`fixed top-0 w-full z-50 backdrop-blur-sm border-b transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-b from-black/90 to-transparent border-white/5' 
        : 'bg-gradient-to-b from-white/95 to-white/80 border-gray-200/50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to="/home" className="flex items-center" data-testid="nav-logo">
            <img 
              src={isDark ? '/logo-white.png' : '/logo-red.png'} 
              alt="Intimate.pt" 
              className="h-8 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href) 
                    ? (isDark ? 'text-white' : 'text-foreground')
                    : (isDark ? 'text-muted-foreground hover:text-white' : 'text-gray-600 hover:text-foreground')
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side - Compact on mobile */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`w-9 h-9 md:w-10 md:h-10 transition-colors ${isDark ? 'text-muted-foreground hover:text-white' : 'text-gray-600 hover:text-foreground'}`}
              title={isDark ? 'Modo Claro' : 'Modo Escuro'}
            >
              {isDark ? (
                <Sun className="w-4 h-4 md:w-5 md:h-5" />
              ) : (
                <Moon className="w-4 h-4 md:w-5 md:h-5" />
              )}
            </Button>

            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/search')}
              data-testid="nav-search"
              className={`w-9 h-9 md:w-10 md:h-10 ${isDark ? 'text-muted-foreground hover:text-white' : 'text-gray-600 hover:text-foreground'}`}
            >
              <Search className="w-4 h-4 md:w-5 md:h-5" />
            </Button>

            {user ? (
              <>
                {/* Notifications Bell */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/inbox')}
                  data-testid="nav-inbox"
                  className="text-muted-foreground hover:text-white relative"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-ruby rounded-full text-xs text-white flex items-center justify-center font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>

                {showEnvelope && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/mensagens')}
                    data-testid="nav-messages"
                    title="Mensagens"
                    className="text-muted-foreground hover:text-white relative"
                  >
                    <Mail className="w-5 h-5" />
                    {im.count > 0 && (
                      <span data-testid="nav-messages-count" className="absolute -top-1 -right-1 w-5 h-5 bg-ruby rounded-full text-xs text-white flex items-center justify-center font-medium">
                        {im.count > 9 ? '9+' : im.count}
                      </span>
                    )}
                  </Button>
                )}

                {/* Premium Badge */}
                {user.is_premium && (
                  <span className="hidden sm:flex items-center gap-1 premium-badge">
                    <Crown className="w-3 h-3" />
                    Premium
                  </span>
                )}

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      data-testid="nav-user-menu"
                      className="text-muted-foreground hover:text-white"
                    >
                      <User className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-surface border-border">
                    <div className="px-3 py-2">
                      <p className="font-medium text-foreground">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      data-testid="menu-profile"
                      className="cursor-pointer"
                    >
                      <User className="w-4 h-4 mr-2" />
                      O Meu Perfil
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/favorites')}
                      data-testid="menu-favorites"
                      className="cursor-pointer"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Favoritos
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/history')}
                      data-testid="menu-history"
                      className="cursor-pointer"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Histórico
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/feed')}
                      data-testid="menu-feed"
                      className="cursor-pointer"
                    >
                      <Rss className="w-4 h-4 mr-2" />
                      O Meu Feed
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/inbox')}
                      data-testid="menu-inbox"
                      className="cursor-pointer"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Notificações
                      {unreadCount > 0 && (
                        <span className="ml-auto bg-ruby text-white text-xs px-2 py-0.5 rounded-full">
                          {unreadCount}
                        </span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/points')}
                      data-testid="menu-points"
                      className="cursor-pointer"
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Os Meus Pontos
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/my-series')}
                      data-testid="menu-my-series"
                      className="cursor-pointer"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      As Minhas Séries
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/my-library')}
                      data-testid="menu-my-library"
                      className="cursor-pointer"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      A Minha Biblioteca
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem 
                      onClick={() => navigate('/write')}
                      data-testid="menu-write"
                      className="cursor-pointer"
                    >
                      <PenLine className="w-4 h-4 mr-2" />
                      Escrever Conto
                    </DropdownMenuItem>
                    {user.is_author && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/author')}
                        data-testid="menu-author"
                        className="cursor-pointer"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Os Meus Contos
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-border" />
                    {user.is_admin && (
                      <>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin')}
                          data-testid="menu-admin-dashboard"
                          className="cursor-pointer text-gold font-semibold"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Painel Admin
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-border" />
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/ads')}
                          data-testid="menu-admin-ads"
                          className="cursor-pointer text-gold"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Gerir Anúncios
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/admin/shop')}
                          data-testid="menu-admin-shop"
                          className="cursor-pointer text-gold"
                        >
                          <ShoppingBag className="w-4 h-4 mr-2" />
                          Gestão da Loja
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/channels')}
                          data-testid="menu-admin-channels"
                          className="cursor-pointer text-gold"
                        >
                          <Radio className="w-4 h-4 mr-2" />
                          Gerir Canais
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/messages')}
                          data-testid="menu-admin-messages"
                          className="cursor-pointer text-gold"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Central de Mensagens
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/points')}
                          data-testid="menu-admin-points"
                          className="cursor-pointer text-gold"
                        >
                          <Award className="w-4 h-4 mr-2" />
                          Gestão de Pontos
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/confessions')}
                          data-testid="menu-admin-confessions"
                          className="cursor-pointer text-gold"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Moderação de Confissões
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/series')}
                          data-testid="menu-admin-series"
                          className="cursor-pointer text-gold"
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          Gestão de Séries
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/conhece-te')}
                          data-testid="menu-admin-conhece-te"
                          className="cursor-pointer text-gold"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Gestão Conhece-te
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-border" />
                      </>
                    )}
                    {!user.is_premium && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/premium')}
                        data-testid="menu-premium"
                        className="cursor-pointer text-gold"
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Assinar Premium
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={logout}
                      data-testid="menu-logout"
                      className="cursor-pointer text-red-400"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  data-testid="nav-login"
                  className="text-muted-foreground hover:text-white"
                >
                  Entrar
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  data-testid="nav-register"
                  className="bg-ruby hover:bg-ruby-light text-white rounded-full px-6"
                >
                  Criar Conta
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-muted-foreground hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-lg font-medium ${
                    isActive(link.href) ? 'text-white' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
